/**
 * Shadow of types and enumerations from `roboto.association`
 */

export enum AssociationType {
  Dataset = "dataset",
  Device = "device",
  DeviceType = "device_type",
  File = "file",
  Topic = "topic",
  MessagePath = "message_path",
  Org = "org",
}

/**
 * The kinds of association that hold files. Each one holds its own tree of
 * folders.
 */
export type FileAssociationType =
  | AssociationType.Dataset
  | AssociationType.Org
  | AssociationType.Device
  | AssociationType.DeviceType;

/**
 * Which kind of entity `associationId` names, read from its `og_`, `dv_` or
 * `dt_` prefix. Every other ID resolves to a dataset, so a dataset ID gets
 * dataset behavior whatever its prefix. The SDK's `Association.from_id`
 * differs: it raises on an unrecognized prefix.
 */
export function fileAssociationType(
  associationId: string,
): FileAssociationType {
  if (associationId.startsWith("og_")) {
    return AssociationType.Org;
  }
  if (associationId.startsWith("dv_")) {
    return AssociationType.Device;
  }
  if (associationId.startsWith("dt_")) {
    return AssociationType.DeviceType;
  }
  return AssociationType.Dataset;
}

export interface AssociationRecord {
  association_id: string;
  association_type: AssociationType;
  association_version?: number | null;
}

export class Association {
  private static URL_ENCODING_SEP = ":";

  #record: AssociationRecord;

  constructor(record: AssociationRecord) {
    this.#record = record;
  }

  public urlEncode(): string {
    const parts = [this.#record.association_type, this.#record.association_id];

    if (this.#record.association_version !== undefined) {
      parts.push(`${this.#record.association_version}`);
    }

    return parts.map(encodeURIComponent).join(Association.URL_ENCODING_SEP);
  }
}
